// src/SupabaseLoginPage.js - 洗練されたデザイン版
import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { useNavigate, Link } from "react-router-dom"; // ← これだけ残す
import { User, Eye, EyeOff, Lock, Building, Key } from "lucide-react";
import { startAuthentication } from "@simplewebauthn/browser";
import TurnstileWidget from "./components/TurnstileWidget";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

function SupabaseLoginPage() {
  const navigate = useNavigate();
  // 🔥 組織ID は端末記憶 (ログアウト・SIGNED_OUT で消さない。 セキュリティ clear と別キー)
  //    最後に成功した組織 ID を自動補完して「毎回打つ」手間をなくす。
  //    ※ セキュリティ上問題ない: 組織 ID が漏れても RLS/JWT で守られているため、
  //      これ単独では別組織に入れない。
  const [organizationId, setOrganizationId] = useState(() => {
    try { return localStorage.getItem("remembered_org_id") || ""; } catch { return ""; }
  });
  const [employeeId, setEmployeeId] = useState(() => {
    try { return localStorage.getItem("remembered_employee_id") || ""; } catch { return ""; }
  });
  const [password, setPassword] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  // リロード後のリダイレクト処理
  useEffect(() => {
    const token = localStorage.getItem('supabase_token');
    const requirePasswordChange = localStorage.getItem('require_password_change');
    
    if (token) {
      console.log("🔄 ログイン完了後のリダイレクト");
      if (requirePasswordChange === 'true') {
        navigate('/change-password');
      } else {
        navigate('/home');
      }
    }
  }, [navigate]);

  // ログインページ用のbodyスタイルを設定
  useEffect(() => {
    const originalBodyStyle = {
      margin: document.body.style.margin,
      padding: document.body.style.padding,
      overflow: document.body.style.overflow,
      backgroundColor: document.body.style.backgroundColor,
    };

    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'auto';

    return () => {
      document.body.style.margin = originalBodyStyle.margin;
      document.body.style.padding = originalBodyStyle.padding;
      document.body.style.overflow = originalBodyStyle.overflow;
      document.body.style.backgroundColor = originalBodyStyle.backgroundColor;
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      setError("セキュリティチェックを完了してください");
      return;
    }

    setLoading(true);
    setError("");

    try {
      console.log("🔐 ログイン試行中...", { organizationId, employeeId });

      // 🛡 セキュリティ: 通常ログイン開始前に、以前の自社スタッフ (Internal) セッションの残骸を強制クリア。
      //    これが残っていると OrganizationContext が別組織の UI として誤って描画してしまう。
      localStorage.removeItem('internal_selected_org');
      localStorage.removeItem('internal_edit_session');

      // 1. まず、カスタムログインAPIでユーザー情報を取得
      const SUPABASE_URL = 'https://tpxzotpgkcaioifgzlii.supabase.co';
      const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRweHpvdHBna2NhaW9pZmd6bGlpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njk0ODI4MTIsImV4cCI6MjA4NTA1ODgxMn0.Re0nm2Nu4BRFf73F9kchoZDjY8BpG1N0Qfe7BtzE1tE';

      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY
          },
          body: JSON.stringify({ organizationId, employeeId, password, captchaToken })
        }
      );

      const responseText = await response.text();
      let responseData;
      
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        console.error("❌ JSON解析エラー:", parseError);
        throw new Error('サーバーからの応答が不正です');
      }

      if (!response.ok) {
        throw new Error(responseData.message || `ログインに失敗しました (${response.status})`);
      }

      if (!responseData.success) {
        throw new Error(responseData.message || 'ログインに失敗しました');
      }

      const { user, requirePasswordChange } = responseData;
      console.log("✅ カスタムログイン成功:", user);

      // 2. Supabase Authで直接ログイン（本物のJWTを取得）
      // CAPTCHA はプロジェクトレベルで有効化されているため captchaToken を渡す
      console.log("🔐 Supabase Auth認証中...", user.email);
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: password,
        options: { captchaToken },
      });

      if (authError) {
        console.error("❌ Supabase Auth認証エラー:", authError);
        throw new Error('認証に失敗しました');
      }

      console.log("✅ Supabase Auth認証成功");

      // 3. 本物のSupabase AuthのトークンをlocalStorageに保存
      const { access_token, refresh_token } = authData.session;
      localStorage.setItem('supabase_token', access_token);
      localStorage.setItem('supabase_refresh_token', refresh_token);
      localStorage.setItem('supabase_user', JSON.stringify(user));

      // 🔥 追加：organizationId と employeeId を個別に保存
        localStorage.setItem('organizationId', user.organizationId);
        localStorage.setItem('employeeId', user.employeeId);
      // 🧠 端末記憶: 次回ログイン時に自動補完するため、別キーに恒久保存 (ログアウトでは消さない)
      try {
        if (user.organizationId) localStorage.setItem('remembered_org_id', user.organizationId);
        if (user.employeeId)     localStorage.setItem('remembered_employee_id', user.employeeId);
      } catch (_) {}

      // 組織情報をユーザーデータから取得（ハードコードなし）
      if (user.organizationId && user.organizationName) {
        const organizationData = {
          id: user.organizationId,
          name: user.organizationName,
          type: user.organizationType || 'hospital'
        };
        localStorage.setItem('currentOrganization', JSON.stringify(organizationData));
      }

      // パスワード変更フラグを設定
      if (requirePasswordChange) {
        localStorage.setItem('require_password_change', 'true');
      }

      // 4. 完全リロードしてOrganizationContextを確実に初期化
      console.log("✅ 認証完了 - ページリロード");
      window.location.reload();

    } catch (error) {
      console.error("❌ ログインエラー:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // パスキーでログイン
  //   1. organizationId + employeeId で passkey-login-begin → challenge + allowCredentials
  //   2. navigator.credentials.get()
  //   3. passkey-login-finish → tokenHash + email
  //   4. supabase.auth.verifyOtp({token_hash, type: 'magiclink'}) で session 発行
  const handlePasskeyLogin = async () => {
    setError("");
    if (!organizationId || !employeeId) {
      setError("パスキー利用には組織 ID と社員番号の入力が必要です");
      return;
    }
    if (!window.PublicKeyCredential) {
      setError("このブラウザはパスキーに対応していません");
      return;
    }
    setPasskeyLoading(true);
    try {
      // 🛡 セキュリティ: 通常ログイン同様、Internal 残骸をクリア (別組織 UI 誤描画防止)
      localStorage.removeItem('internal_selected_org');
      localStorage.removeItem('internal_edit_session');

      const SUPABASE_URL = 'https://tpxzotpgkcaioifgzlii.supabase.co';
      const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRweHpvdHBna2NhaW9pZmd6bGlpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njk0ODI4MTIsImV4cCI6MjA4NTA1ODgxMn0.Re0nm2Nu4BRFf73F9kchoZDjY8BpG1N0Qfe7BtzE1tE';

      // 1. challenge 取得
      const beginRes = await fetch(`${SUPABASE_URL}/functions/v1/passkey-login-begin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ organizationId, employeeId }),
      });
      const begin = await beginRes.json();
      console.log("🔑 passkey-login-begin response:", begin);
      if (!beginRes.ok || !begin.success) {
        throw new Error(begin?.message || 'チャレンジ取得失敗');
      }
      if (!begin.hasPasskeys) {
        throw new Error('このアカウントにパスキーが登録されていません');
      }
      if (!begin?.options?.challenge) {
        throw new Error(`サーバーが不正な options を返しました: ${JSON.stringify(begin?.options)}`);
      }

      // 2. ブラウザのパスキー認証
      //    v10.0.0 は options を直接渡す API (後の v11+ は {optionsJSON: ...})。
      const assertion = await startAuthentication(begin.options);

      // 3. 検証 + session 発行
      const finishRes = await fetch(`${SUPABASE_URL}/functions/v1/passkey-login-finish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ challengeId: begin.challengeId, assertionResponse: assertion }),
      });
      const finish = await finishRes.json();
      if (!finishRes.ok || !finish.success) {
        throw new Error(finish?.message || 'パスキー検証失敗');
      }

      // 4. magic link の token_hash で session 発行
      const { data: otpData, error: otpErr } = await supabase.auth.verifyOtp({
        token_hash: finish.tokenHash,
        type: 'magiclink',
      });
      if (otpErr || !otpData?.session) {
        throw new Error(`セッション確立失敗: ${otpErr?.message || ''}`);
      }

      // 5. 既存フローと同じく localStorage に保存
      const user = finish.user;
      const { access_token, refresh_token } = otpData.session;
      localStorage.setItem('supabase_token', access_token);
      localStorage.setItem('supabase_refresh_token', refresh_token);
      localStorage.setItem('supabase_user', JSON.stringify(user));
      localStorage.setItem('organizationId', user.organizationId);
      localStorage.setItem('employeeId', user.employeeId);
      // 🧠 端末記憶 (次回自動補完用)
      try {
        if (user.organizationId) localStorage.setItem('remembered_org_id', user.organizationId);
        if (user.employeeId)     localStorage.setItem('remembered_employee_id', user.employeeId);
      } catch (_) {}
      if (user.organizationId) {
        const organizationData = {
          id: user.organizationId,
          name: user.organizationName || user.organizationId,
          type: user.organizationType || 'hospital',
        };
        localStorage.setItem('currentOrganization', JSON.stringify(organizationData));
      }
      if (finish.requirePasswordChange) {
        localStorage.setItem('require_password_change', 'true');
      }

      window.location.reload();
    } catch (e) {
      if (e.name === 'NotAllowedError' || /cancell?ed|aborted/i.test(e.message || '')) {
        setError('キャンセルされました');
      } else {
        setError(`パスキーログイン失敗: ${e.message || e}`);
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  return (
    <>
      {/* 背景グラデーション + 装飾パターン（MS&Iブランドカラー） */}
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "linear-gradient(135deg, #1a365d 0%, #2563eb 100%)",
        zIndex: 10000,
        overflow: "auto",
      }}>
        {/* 装飾的な背景パターン（動的アニメーション） */}
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity: 0.12,
          background: `
            radial-gradient(circle at 20% 30%, rgba(255,255,255,0.25) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(255,255,255,0.15) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(255,255,255,0.2) 0%, transparent 50%)
          `,
          animation: "float 20s ease-in-out infinite",
        }}></div>

        {/* メインコンテンツコンテナ */}
        <div style={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "3rem 1.5rem",
          boxSizing: "border-box",
        }}>
        
        {/* メインロゴエリア */}
        <div style={{
          textAlign: "center",
          marginBottom: "2.5rem",
          maxWidth: "500px",
          width: "100%",
          animation: "fadeInDown 0.8s ease-out",
        }}>
          <h1 style={{ 
            fontSize: "clamp(2.5rem, 8vw, 4rem)", 
            marginBottom: "0.5rem",
            color: "white",
            fontWeight: "800",
            letterSpacing: "-2px",
            textShadow: "0 4px 30px rgba(0,0,0,0.3)",
            lineHeight: "1.1",
          }}>
            ScrubEdge
          </h1>
          <p style={{ 
            fontSize: "clamp(0.9rem, 2.5vw, 1.1rem)", 
            color: "rgba(255, 255, 255, 0.95)", 
            marginBottom: "0",
            lineHeight: "1.6",
            fontWeight: "500",
            textShadow: "0 2px 15px rgba(0,0,0,0.2)",
            padding: "0 1rem",
          }}>
            ORナースのための次世代スマート管理プラットフォーム
          </p>
        </div>

        {/* エラー表示 */}
        {error && (
          <div style={{
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            border: "2px solid #f87171",
            color: "#dc2626",
            padding: "1.25rem 1.5rem",
            borderRadius: "16px",
            marginBottom: "1.5rem",
            textAlign: "center",
            maxWidth: "450px",
            width: "100%",
            fontSize: "0.95rem",
            lineHeight: "1.5",
            whiteSpace: "pre-line",
            boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            animation: "shake 0.5s ease-in-out",
          }}>
            <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>⚠️</div>
            {error}
          </div>
        )}

        {/* ログインフォーム */}
        <div style={{
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          padding: "2.5rem 2rem",
          borderRadius: "24px",
          textAlign: "center",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255,255,255,0.1)",
          maxWidth: "450px",
          width: "100%",
          marginBottom: "2rem",
          backdropFilter: "blur(20px)",
          animation: "fadeInUp 0.8s ease-out 0.2s backwards",
        }}>
          <form onSubmit={handleLogin}>
            {/* 組織ID入力 */}
            <div style={{ marginBottom: "1.5rem", textAlign: "left" }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: "0.6rem",
              }}>
                <label style={{
                  display: "block",
                  fontSize: "0.9rem",
                  fontWeight: "700",
                  color: "#1f2937",
                  letterSpacing: "0.3px",
                }}>
                  組織ID
                </label>
                {(organizationId || employeeId) && (
                  <button
                    type="button"
                    onClick={() => {
                      setOrganizationId("");
                      setEmployeeId("");
                      try {
                        localStorage.removeItem("remembered_org_id");
                        localStorage.removeItem("remembered_employee_id");
                      } catch (_) {}
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#64748b",
                      fontSize: "0.75rem",
                      cursor: "pointer",
                      padding: 0,
                      textDecoration: "underline",
                    }}
                    title="記憶した組織ID・社員番号を削除"
                  >
                    記憶をクリア
                  </button>
                )}
              </div>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  value={organizationId}
                  onChange={(e) => setOrganizationId(e.target.value)}
                  placeholder=""
                  required
                  style={{
                    width: "100%",
                    padding: "1rem 3rem 1rem 1rem",
                    fontSize: "1rem",
                    border: "2px solid #e5e7eb",
                    borderRadius: "12px",
                    outline: "none",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#2563eb";
                    e.target.style.boxShadow = "0 0 0 4px rgba(37, 99, 235, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e5e7eb";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <span style={{
                  position: "absolute",
                  right: "14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#9ca3af",
                  display: "flex",
                  alignItems: "center",
                  pointerEvents: "none",
                }}>
                  <Building size={20} />
                </span>
              </div>
            </div>

            {/* 社員番号入力 */}
            <div style={{ marginBottom: "1.5rem", textAlign: "left" }}>
              <label style={{
                display: "block",
                fontSize: "0.9rem",
                fontWeight: "700",
                color: "#1f2937",
                marginBottom: "0.6rem",
                letterSpacing: "0.3px",
              }}>
                社員番号
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  placeholder=""
                  required
                  style={{
                    width: "100%",
                    padding: "1rem 3rem 1rem 1rem",
                    fontSize: "1rem",
                    border: "2px solid #e5e7eb",
                    borderRadius: "12px",
                    outline: "none",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#2563eb";
                    e.target.style.boxShadow = "0 0 0 4px rgba(37, 99, 235, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e5e7eb";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <span style={{
                  position: "absolute",
                  right: "14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#9ca3af",
                  display: "flex",
                  alignItems: "center",
                  pointerEvents: "none",
                }}>
                  <User size={20} />
                </span>
              </div>
            </div>

            {/* パスワード入力 */}
            <div style={{ marginBottom: "2rem", textAlign: "left" }}>
              <label style={{
                display: "block",
                fontSize: "0.9rem",
                fontWeight: "700",
                color: "#1f2937",
                marginBottom: "0.6rem",
                letterSpacing: "0.3px",
              }}>
                パスワード
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder=""
                  required
                  style={{
                    width: "100%",
                    padding: "1rem 3rem 1rem 1rem",
                    fontSize: "1rem",
                    border: "2px solid #e5e7eb",
                    borderRadius: "12px",
                    outline: "none",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#2563eb";
                    e.target.style.boxShadow = "0 0 0 4px rgba(37, 99, 235, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e5e7eb";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "6px",
                    display: "flex",
                    alignItems: "center",
                    color: "#6b7280",
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = "#2563eb"}
                  onMouseLeave={(e) => e.currentTarget.style.color = "#6b7280"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Turnstile (人間確認) */}
            <div style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "1.5rem",
              minHeight: "65px",
            }}>
              <TurnstileWidget
                siteKey={TURNSTILE_SITE_KEY}
                onVerify={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken("")}
                onError={() => setCaptchaToken("")}
              />
            </div>

            {/* ログインボタン */}
            <button
              type="submit"
              disabled={loading || !captchaToken}
              style={{
                width: "100%",
                padding: "1.1rem 2rem",
                background: loading 
                  ? "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)"
                  : "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
                color: "white",
                border: "none",
                borderRadius: "12px",
                fontSize: "1.1rem",
                fontWeight: "700",
                cursor: loading ? "not-allowed" : "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: loading 
                  ? "none" 
                  : "0 10px 30px rgba(37, 99, 235, 0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.75rem",
                letterSpacing: "0.5px",
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 15px 40px rgba(37, 99, 235, 0.5)";
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 10px 30px rgba(37, 99, 235, 0.4)";
                }
              }}
            >
              {loading ? (
                <>
                  <div style={{
                    display: "inline-block",
                    width: "18px",
                    height: "18px",
                    border: "3px solid rgba(255,255,255,0.3)",
                    borderTop: "3px solid white",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}></div>
                  ログイン中...
                </>
              ) : (
                <>
                  <Lock size={20} />
                  ログイン
                </>
              )}
            </button>

            {/* パスキーでログイン */}
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              margin: "1rem 0 0.75rem",
              color: "#94a3b8",
              fontSize: "0.75rem",
            }}>
              <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
              <span>または</span>
              <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
            </div>

            <button
              type="button"
              onClick={handlePasskeyLogin}
              disabled={passkeyLoading || loading}
              style={{
                width: "100%",
                padding: "0.95rem 2rem",
                background: "white",
                color: "#1d4ed8",
                border: "2px solid #1d4ed8",
                borderRadius: "12px",
                fontSize: "1rem",
                fontWeight: "700",
                cursor: (passkeyLoading || loading) ? "not-allowed" : "pointer",
                opacity: (passkeyLoading || loading) ? 0.6 : 1,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.6rem",
              }}
            >
              <Key size={18} />
              {passkeyLoading ? "認証中..." : "パスキーでログイン"}
            </button>
            <p style={{
              fontSize: "0.7rem",
              color: "#94a3b8",
              textAlign: "center",
              margin: "0.4rem 0 0",
              lineHeight: 1.5,
            }}>
              Touch ID / Face ID / セキュリティキーで安全にサインイン<br />
              (事前に設定 → パスキー から登録が必要です)
            </p>

            <div className="text-center mt-4">
            <Link
                to="/forgot-password"
                className="text-sm text-teal-600 hover:text-teal-700 font-medium"
            >
                パスワードをお忘れですか？
            </Link>
            </div>
          </form>
        </div>

        {/* フッター情報 */}
        <div style={{ 
          fontSize: "0.85rem", 
          color: "white", 
          textAlign: "center",
          maxWidth: "600px",
          width: "100%",
          animation: "fadeIn 0.8s ease-out 0.4s backwards",
        }}>
          <div style={{
            backgroundColor: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            padding: "1.5rem",
            borderRadius: "16px",
            marginBottom: "1rem",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}>
            <p style={{ 
              marginBottom: "1rem", 
              fontSize: "0.9rem", 
              color: "rgba(255, 255, 255, 0.95)",
              lineHeight: "1.5",
            }}>
              💡 事前に登録された組織ID・社員番号・パスワードでログインしてください
            </p>
            <div style={{ marginBottom: "0.5rem" }}>
              <a href="https://scrubedge.com/terms/" target="_blank" rel="noopener noreferrer" style={{ 
                marginRight: "1.5rem", 
                textDecoration: "none",
                color: "white",
                fontWeight: "600",
                transition: "opacity 0.2s",
                opacity: 0.9,
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
              onMouseLeave={(e) => e.currentTarget.style.opacity = "0.9"}
              >
                利用規約
              </a>
              <a href="https://scrubedge.com/policy/" target="_blank" rel="noopener noreferrer" style={{ 
                marginRight: "1.5rem", 
                textDecoration: "none",
                color: "white",
                fontWeight: "600",
                transition: "opacity 0.2s",
                opacity: 0.9,
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
              onMouseLeave={(e) => e.currentTarget.style.opacity = "0.9"}
              >
                プライバシーポリシー
              </a>
              <a href="https://scrubedge.com/contact/" target="_blank" rel="noopener noreferrer" style={{ 
                textDecoration: "none",
                color: "white",
                fontWeight: "600",
                transition: "opacity 0.2s",
                opacity: 0.9,
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
              onMouseLeave={(e) => e.currentTarget.style.opacity = "0.9"}
              >
                お問い合わせ
              </a>
            </div>
          </div>
          
          <div style={{ 
            fontSize: "0.75rem", 
            color: "rgba(255, 255, 255, 0.7)",
            fontWeight: "500",
          }}>
            © 2026 ScrubEdge. All rights reserved.
          </div>
        </div>
        </div>
      </div>
      
      {/* アニメーション用スタイル */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -30px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }
        
        ::-webkit-scrollbar {
          width: 10px;
        }
        
        ::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.1);
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.3);
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.5);
        }
      `}</style>
    </>
  );
}

export default SupabaseLoginPage;